import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Save, 
  Trash2, 
  Search, 
  X,
  Database,
  Building,
  MapPin,
  DollarSign,
  Users,
  GraduationCap,
  Download,
  Upload,
  Edit3,
  Check,
  AlertCircle,
  Link as LinkIcon
} from 'lucide-react';
import { collegeService } from '../lib/database';
import type { College } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SupabaseCollegeManager = () => {
  const { user, isAdmin } = useAuth();
  const [colleges, setColleges] = useState<College[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newCollege, setNewCollege] = useState({
    name: '',
    slug: '',
    description: '',
    location: '',
    state: '',
    category: 'Engineering',
    stream: 'Engineering',
    rating: 4.0,
    fees: '',
    cutoff: '',
    established: '',
    type: 'Government',
    image_url: 'https://images.pexels.com/photos/1454360/pexels-photo-1454360.jpeg?auto=compress&cs=tinysrgb&w=800',
    contact_phone: '',
    contact_email: '',
    contact_website: '',
    courses: [] as string[],
    specializations: [] as string[],
    facilities: [] as string[]
  });

  const categories = ['Engineering', 'Medical', 'Law', 'Management', 'Arts', 'Commerce', 'Science'];
  const states = ['Delhi', 'Maharashtra', 'Karnataka', 'Tamil Nadu', 'Rajasthan', 'Gujarat', 'West Bengal', 'Uttar Pradesh', 'Haryana', 'Punjab'];
  const types = ['Government', 'Private'];

  const defaultCourses = {
    Engineering: ['B.Tech Computer Science', 'B.Tech Electronics', 'B.Tech Mechanical', 'B.Tech Civil', 'B.Tech Electrical'],
    Medical: ['MBBS', 'BDS', 'BAMS', 'BHMS', 'B.Pharma'],
    Management: ['MBA', 'BBA', 'PGDM', 'Executive MBA'],
    Law: ['BA LLB', 'BBA LLB', 'LLM', 'B.Com LLB'],
    Arts: ['BA', 'MA', 'B.Fine Arts', 'M.Fine Arts'],
    Commerce: ['B.Com', 'M.Com', 'BBA', 'MBA'],
    Science: ['BSc', 'MSc', 'B.Tech', 'M.Tech']
  };

  const defaultSpecializations = {
    Engineering: ['Computer Science', 'Electronics & Communication', 'Mechanical', 'Civil', 'Electrical'],
    Medical: ['General Medicine', 'Surgery', 'Pediatrics', 'Cardiology', 'Dermatology'],
    Management: ['Finance', 'Marketing', 'Operations', 'HR', 'Strategy'],
    Law: ['Corporate Law', 'Criminal Law', 'Constitutional Law', 'International Law'],
    Arts: ['Literature', 'History', 'Philosophy', 'Psychology', 'Sociology'],
    Commerce: ['Accounting', 'Finance', 'Economics', 'Business Studies'],
    Science: ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'Computer Science']
  };

  const defaultFacilities = [
    'Central Library', 'Computer Labs', 'Research Centers', 'Sports Complex', 
    'Hostels', 'Medical Center', 'Cafeteria', 'Auditorium', 'Workshop', 'Gymnasium'
  ];

  useEffect(() => {
    if (isAdmin) {
      fetchColleges();
    }
  }, [isAdmin, searchQuery]);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const data = await collegeService.getAll({ search: searchQuery });
      setColleges(data);
    } catch (error) {
      console.error('Error fetching colleges:', error);
      toast.error('Failed to fetch colleges');
    } finally {
      setLoading(false);
    }
  };

  // Generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleInputChange = (field: string, value: any) => {
    setNewCollege(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug when name changes
    if (field === 'name' && value) {
      const autoSlug = generateSlug(value);
      setNewCollege(prev => ({
        ...prev,
        slug: autoSlug
      }));
    }

    // Auto-populate courses and specializations based on category
    if (field === 'category') {
      setNewCollege(prev => ({
        ...prev,
        stream: value,
        courses: defaultCourses[value as keyof typeof defaultCourses] || [],
        specializations: defaultSpecializations[value as keyof typeof defaultSpecializations] || []
      }));
    }
  };

  const handleArrayChange = (field: 'courses' | 'specializations' | 'facilities', value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    setNewCollege(prev => ({
      ...prev,
      [field]: items
    }));
  };

  const resetForm = () => {
    setNewCollege({
      name: '',
      slug: '',
      description: '',
      location: '',
      state: '',
      category: 'Engineering',
      stream: 'Engineering',
      rating: 4.0,
      fees: '',
      cutoff: '',
      established: '',
      type: 'Government',
      image_url: 'https://images.pexels.com/photos/1454360/pexels-photo-1454360.jpeg?auto=compress&cs=tinysrgb&w=800',
      contact_phone: '',
      contact_email: '',
      contact_website: '',
      courses: defaultCourses.Engineering,
      specializations: defaultSpecializations.Engineering,
      facilities: defaultFacilities
    });
  };

  const validateForm = () => {
    if (!newCollege.name.trim()) {
      toast.error('College name is required');
      return false;
    }
    if (!newCollege.slug.trim()) {
      toast.error('Slug is required');
      return false;
    }
    if (!newCollege.location.trim()) {
      toast.error('Location is required');
      return false;
    }
    if (!newCollege.state) {
      toast.error('State is required');
      return false;
    }
    if (!newCollege.fees.trim()) {
      toast.error('Fees information is required');
      return false;
    }
    if (!newCollege.cutoff.trim()) {
      toast.error('Cutoff information is required');
      return false;
    }
    if (!newCollege.established.trim()) {
      toast.error('Establishment year is required');
      return false;
    }
    return true;
  };

  const saveCollege = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const collegeData = {
        ...newCollege,
        description: newCollege.description || `${newCollege.category} college located in ${newCollege.location}, ${newCollege.state}.`
      };

      if (editingId) {
        await collegeService.update(editingId, collegeData);
        toast.success('College updated successfully!');
        setEditingId(null);
      } else {
        await collegeService.create(collegeData);
        toast.success('College added successfully!');
        setShowAddForm(false);
      }
      
      resetForm();
      fetchColleges();
    } catch (error) {
      console.error('Error saving college:', error);
      toast.error('Failed to save college. Please check your permissions.');
    } finally {
      setSaving(false);
    }
  };

  const editCollege = (college: College) => {
    setNewCollege({
      name: college.name,
      slug: college.slug,
      description: college.description,
      location: college.location,
      state: college.state,
      category: college.category,
      stream: college.stream,
      rating: college.rating,
      fees: college.fees,
      cutoff: college.cutoff,
      established: college.established,
      type: college.type,
      image_url: college.image_url,
      contact_phone: college.contact_phone || '',
      contact_email: college.contact_email || '',
      contact_website: college.contact_website || '',
      courses: college.courses || [],
      specializations: college.specializations || [],
      facilities: college.facilities || []
    });
    setEditingId(college.id);
    setShowAddForm(true);
  };

  const deleteCollege = async (college: College) => {
    if (!confirm(`Are you sure you want to delete ${college.name}?`)) return;

    try {
      await collegeService.delete(college.id);
      setColleges(prev => prev.filter(c => c.id !== college.id));
      toast.success('College deleted successfully!');
    } catch (error) {
      console.error('Error deleting college:', error);
      toast.error('Failed to delete college');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    resetForm();
  };

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <AlertCircle className="h-16 w-16 text-warning mx-auto mb-3" />
                </div>
                <h1 className="h2 text-dark mb-3">Access Restricted</h1>
                <p className="text-muted mb-4">
                  You need admin privileges to access the College Management system.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <Link to="/" className="btn btn-primary">
                    Back to Home
                  </Link>
                  <Link to="/contact" className="btn btn-outline-primary">
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading college management...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary rounded-3 p-3">
              <Database className="text-white" size={32} />
            </div>
            <div>
              <h1 className="h2 fw-bold text-dark mb-1">Supabase College Manager</h1>
              <p className="text-muted mb-0">Direct database management for college information</p>
            </div>
          </div>
          <Link to="/" className="btn btn-outline-secondary">
            Back to Home
          </Link>
        </div>

        {/* Action Bar */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div className="d-flex gap-2">
            <button
              onClick={() => {
                resetForm();
                setShowAddForm(true);
                setEditingId(null);
              }}
              className="btn btn-success d-flex align-items-center gap-2"
            >
              <Plus size={16} />
              Add New College
            </button>
            <span className="badge bg-primary fs-6 px-3 py-2">
              {colleges.length} Colleges
            </span>
          </div>

          <div className="position-relative">
            <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
            <input
              type="text"
              placeholder="Search colleges..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control ps-5"
              style={{ width: '300px' }}
            />
          </div>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card border-0 shadow-lg mb-4"
          >
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">{editingId ? 'Edit College' : 'Add New College'}</h5>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">College Name *</label>
                  <input
                    type="text"
                    value={newCollege.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="form-control"
                    placeholder="Enter college name"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    URL Slug * 
                    <small className="text-muted">(SEO-friendly URL)</small>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <LinkIcon size={16} />
                    </span>
                    <input
                      type="text"
                      value={newCollege.slug}
                      onChange={(e) => handleInputChange('slug', e.target.value)}
                      className="form-control"
                      placeholder="college-name-slug"
                    />
                  </div>
                  <div className="form-text">
                    Preview: /college/{newCollege.slug || 'your-slug-here'}
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Location *</label>
                  <input
                    type="text"
                    value={newCollege.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="form-control"
                    placeholder="City, State"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">State *</label>
                  <select
                    value={newCollege.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Category *</label>
                  <select
                    value={newCollege.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="form-select"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Type *</label>
                  <select
                    value={newCollege.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="form-select"
                  >
                    {types.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Rating</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={newCollege.rating}
                    onChange={(e) => handleInputChange('rating', parseFloat(e.target.value))}
                    className="form-control"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Fees *</label>
                  <input
                    type="text"
                    value={newCollege.fees}
                    onChange={(e) => handleInputChange('fees', e.target.value)}
                    className="form-control"
                    placeholder="e.g., ₹2.5L/year"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Established *</label>
                  <input
                    type="text"
                    value={newCollege.established}
                    onChange={(e) => handleInputChange('established', e.target.value)}
                    className="form-control"
                    placeholder="e.g., 1961"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Cutoff Information *</label>
                  <input
                    type="text"
                    value={newCollege.cutoff}
                    onChange={(e) => handleInputChange('cutoff', e.target.value)}
                    className="form-control"
                    placeholder="e.g., JEE Advanced Rank 1-500"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Description</label>
                  <textarea
                    value={newCollege.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="form-control"
                    rows={3}
                    placeholder="Brief description of the college..."
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Contact Phone</label>
                  <input
                    type="text"
                    value={newCollege.contact_phone}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                    className="form-control"
                    placeholder="+91-11-2659-1000"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Contact Email</label>
                  <input
                    type="email"
                    value={newCollege.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    className="form-control"
                    placeholder="info@college.edu"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Website</label>
                  <input
                    type="url"
                    value={newCollege.contact_website}
                    onChange={(e) => handleInputChange('contact_website', e.target.value)}
                    className="form-control"
                    placeholder="https://college.edu"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Image URL</label>
                  <input
                    type="url"
                    value={newCollege.image_url}
                    onChange={(e) => handleInputChange('image_url', e.target.value)}
                    className="form-control"
                    placeholder="https://images.pexels.com/..."
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Courses (comma-separated)</label>
                  <textarea
                    value={newCollege.courses.join(', ')}
                    onChange={(e) => handleArrayChange('courses', e.target.value)}
                    className="form-control"
                    rows={3}
                    placeholder="B.Tech, M.Tech, PhD"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Specializations (comma-separated)</label>
                  <textarea
                    value={newCollege.specializations.join(', ')}
                    onChange={(e) => handleArrayChange('specializations', e.target.value)}
                    className="form-control"
                    rows={3}
                    placeholder="Computer Science, Electronics"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Facilities (comma-separated)</label>
                  <textarea
                    value={newCollege.facilities.join(', ')}
                    onChange={(e) => handleArrayChange('facilities', e.target.value)}
                    className="form-control"
                    rows={3}
                    placeholder="Library, Labs, Hostels"
                  />
                </div>
              </div>
              
              <div className="d-flex gap-2 mt-4">
                <button
                  onClick={saveCollege}
                  disabled={saving}
                  className="btn btn-primary d-flex align-items-center gap-2"
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingId ? 'Update College' : 'Save College'}
                    </>
                  )}
                </button>
                <button
                  onClick={cancelEdit}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* College List */}
        <div className="row g-4">
          {colleges.map((college) => (
            <div key={college.id} className="col-12">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card border-0 shadow-sm"
              >
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <img
                        src={college.image_url}
                        alt={college.name}
                        className="img-fluid rounded"
                        style={{ height: '120px', width: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <div className="col-md-6">
                      <h5 className="fw-bold text-dark mb-2">{college.name}</h5>
                      <div className="vstack gap-1 small text-muted">
                        <div className="d-flex align-items-center gap-2">
                          <MapPin size={14} />
                          <span>{college.location}, {college.state}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Building size={14} />
                          <span>{college.category} • {college.type}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <DollarSign size={14} />
                          <span>{college.fees}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Users size={14} />
                          <span>Established: {college.established}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <LinkIcon size={14} />
                          <span className="text-primary">/college/{college.slug}</span>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span className="badge bg-primary me-2">Rating: {college.rating}/5</span>
                        <span className="badge bg-secondary">{college.courses?.length || 0} Courses</span>
                      </div>
                    </div>
                    <div className="col-md-3 text-end">
                      <div className="d-flex flex-column gap-2">
                        <Link
                          to={`/college/${college.slug}`}
                          target="_blank"
                          className="btn btn-outline-info btn-sm d-flex align-items-center gap-2"
                        >
                          <LinkIcon size={14} />
                          View Page
                        </Link>
                        <button
                          onClick={() => editCollege(college)}
                          className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                        >
                          <Edit3 size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteCollege(college)}
                          className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>

        {colleges.length === 0 && !loading && (
          <div className="text-center py-5">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <GraduationCap className="text-primary mx-auto mb-3" size={64} />
                </div>
                <h3 className="h4 text-dark mb-3">No colleges found</h3>
                <p className="text-muted mb-4">
                  {searchQuery 
                    ? 'No colleges match your search criteria. Try adjusting your search terms.'
                    : 'Get started by adding your first college to the database.'
                  }
                </p>
                <button
                  onClick={() => {
                    resetForm();
                    setShowAddForm(true);
                    setEditingId(null);
                  }}
                  className="btn btn-primary"
                >
                  Add Your First College
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupabaseCollegeManager;